import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  FacilityKbn,
  FacilityKind,
  formClassFacility,
  FormKbn,
  GeneralFacilityType,
  Prefectures,
  Status,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import TkStepper from "../../components/atoms/TkStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import TkDateDialog from "../../components/molecules/TkDateDialog";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { TkGetCode } from "../../utils/TkGetCode";
import { changeZipcodeFormat } from "../../utils/useTools";
import { IAPI_FAC016_Request, IAPI_FAC025_Response } from "../../type/apiType";
import { httpIAPI_FAC016, httpIAPI_FAC025 } from "./api/request";
import { schema, steps, tableList } from "./parts";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";
import facilitiesfields from "../../assets/data/TkFacilitySelectionProperty";
import _ from "lodash";
import { TkRouteContext } from "../../context/tkRouteContext";

/**
 * SCRC050017 施設情報の変更登録
 * 施設基本情報を更新する。
 * @return SCRC050017
 */
const SCRC050017 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);

  const isKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;

  const [stateTableList, setStateTableList] = useState<any>([]);
  const [stateSchema, setStateSchema] = useState<any>(schema.common);

  const [res, setRes] = useState<IAPI_FAC025_Response>({});

  const [nameChange, setNameChange] = useState(false);
  const [addressChange, setAddressChange] = useState(false);
  const [classChange, setClassChange] = useState(false);
  const [joinFlg, setJoinFlg] = useState(false);

  const [facilityClass, setFacilityClass] = useState("");

  const [showButton, setShowButton] = useState(false);
  const [openSave, setOpenSave] = useState(false);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);

  const [activeStep, setActiveStep] = useState(0);

  const httpReq = useRef<any>({});
  const facilities = useRef<any[] | undefined>([]);
  const stateFakeTableList = useRef(_.cloneDeep(tableList));

  const { state: routerState, setState: setRouterState } =
    useContext(TkRouteContext);
  const wantToSetInRouterList = useRef<any>(
    location.state?.wantToSetInRouterList ?? []
  );

  useEffect(() => {
    if (wantToSetInRouterList.current.length === 0) {
      return;
    }
    if (_.isEqual(wantToSetInRouterList.current, routerState.routerList)) {
      return;
    }
    setRouterState({
      routerList: wantToSetInRouterList.current as any,
    });
  }, [wantToSetInRouterList, routerState.routerList, setRouterState]);

  // 初期表示
  useEffect(() => {
    let tableArr = stateFakeTableList.current;
    let contractorName = tableArr.find(
      (item) => item.keyword === "contractorName"
    )!;

    if (isKiko) {
      contractorName.showLine = true;
      contractorName.contractorNumber = `${businessState.common?.contractorNo}:${businessState.common?.contractorName}`;
    }

    setStateTableList([...tableArr]);
    handleCallChangeFacilityApi(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallChangeFacilityApi = async (facilityNo?: any) => {
    // 施設情報の変更登録表示API呼び出し
    try {
      const res = await httpIAPI_FAC025({
        contractorNo: isKiko
          ? businessState.common?.contractorNo
          : state.authInfo?.contractorNo,
        applyId: location.state?.applyId,
        facilityNo: facilityNo,
      });

      if (res.resultCd === "I0000") {
        let tableArr = stateFakeTableList.current;

        const tobeDownload =
          res.latestApplyStatus !== "00" &&
          res.latestApplyStatus !== "16" &&
          res.latestApplyStatus !== null;

        let facilityName = tableArr.find(
          (item) => item.keyword === "facilityName"
        )!;
        let changes = tableArr.find((item) => item.keyword === "changes")!;
        let bZipcode = tableArr.find((item) => item.keyword === "bZipcode")!;
        let bAddress = tableArr.find((item) => item.keyword === "bAddress")!;
        let bFacilityClassCd = tableArr.find(
          (item) => item.keyword === "bFacilityClassCd"
        )!;
        let form = tableArr.find((item) => item.keyword === "form")!;

        let postcode = tableArr.find((item) => item.keyword === "aZipcode")!;
        let address = tableArr.find((item) => item.keyword === "aAddress")!;

        let aFacilityClassCd = tableArr.find(
          (item) => item.keyword === "aFacilityClassCd"
        )!;

        let bFacilityName = tableArr.find(
          (item) => item.keyword === "bFacilityName"
        )!;

        const facility = res.facilityList?.[0];
        const selectPullDown = res.facilityList?.map((item) => ({
          value: item.facilityNo,
          label: `${item.facilityNo}:${item.bFacilityName}`,
        }));
        if (facilities.current && facilities.current.length === 0) {
          facilityName.setDefaultValue = facility?.facilityNo;
          facilityName.selectPropsList = selectPullDown;
          if (!res.applyId) {
            facilityName.setDefaultValue = "";
          }
          facilities.current = selectPullDown as any[];
        }

        // 初期表示
        for (let i in facility) {
          let targetObj = tableArr.find((item) => item.keyword === i)!;

          targetObj &&
            (targetObj.setDefaultValue = facility[i as keyof typeof facility]);
          targetObj && (targetObj.text = facility[i as keyof typeof facility]);
        }

        if (!facilityNo && !res.applyId) {
          bFacilityName.setDefaultValue = "";
          bFacilityName.text = "";
        }

        // 旧：郵便番号
        bZipcode.text = changeZipcodeFormat(facility?.bZipcode);

        // 旧：所在地
        bAddress.text = `${
          TkGetCode(Prefectures, facility?.bPrefectureCd) ?? ""
        }${facility?.bCity ?? ""}${facility?.bAdressOthers ?? ""}`;

        // 新：所在地
        tableArr.find((item) => item.keyword === "aAddress")!.setDefaultValue =
          {
            prefectures: TkGetCode(Prefectures, facility?.aPrefectureCd),
            city: facility?.aCity!,
            address: facility?.aAdressOthers!,
          };

        // 旧：施設種類
        bFacilityClassCd.text =
          FacilityKind[facility?.bFacilityClassCd as keyof typeof FacilityKind];

        // 添付書類
        let targetForm = res.formList?.find(
          (item: any) =>
            item.formKbn === FormKbn["施設書類"] &&
            item.formClass ===
              formClassFacility["施設情報の変更事項が確認できる書類"]
        )!;

        if (targetForm) {
          if (tobeDownload) {
            form.type = "downloadLink";
            form.downloadBase64 = true;
            form.dataname = targetForm.fileName;
            form.base64filedata = targetForm.fileBase64;
          } else {
            form.setDefaultValue = [
              {
                formName: targetForm?.fileName,
                fileBase64: targetForm?.fileBase64,
              },
            ];
          }
        }

        // 施設名称　選択
        facilityName.onGetValue = async (e: any) => {
          handleCallChangeFacilityApi(e);
          httpReq.current.selectedFacilityNo = e;
        };

        // 住所検索
        postcode.getPostalCodeValue = (value: any) => {
          const data = value.value;

          address.valueObject = {
            prefectures: data.address1,
            city: `${data.address2}${data.address3}`,
            address: "",
          };

          setState({
            ...state,
            errorMsgId: "",
          });
        };

        postcode.getPostalCodeError = (value: any) => {
          setState({
            ...state,
            errorMsgId: value,
          });
        };

        // 変更事項
        changes.onGetValue = (e: any) => {
          setNameChange(e.includes("施設名称の変更"));
          setAddressChange(e.includes("施設の所在地の変更"));
          setClassChange(e.includes("施設種類の変更"));
        };

        // 新：施設種類
        aFacilityClassCd.getFacilityList = (e: any) => {
          setJoinFlg(
            (facility?.bFacilityClassCd === "0301" ||
              facility?.bFacilityClassCd === "0303") &&
              e.typecode ===
                GeneralFacilityType[
                  "軽費老人ホーム（介護保険法の規定に基づく指定に係るもの）"
                ]
          );
          setFacilityClass(e.typecode);
        };
        setRes(res);

        // 申請ステータスによる制御
        if (res.applyId) {
          if (
            res.latestApplyStatus === Status["届出待ち"] ||
            res.latestApplyStatus === Status["差戻し一時保存"]
          ) {
            facilityName.disabled = true;
            setShowButton(true);
          } else {
            setActiveStep(1);
            tableArr.forEach((item) => (item.disabled = true));
            let checkValue = [];
            if (res.facilityList?.[0]?.nameChangeDate) {
              checkValue.push("施設名称の変更");
              setNameChange(true);
            }
            if (res.facilityList?.[0]?.addressChangeDate) {
              checkValue.push("施設の所在地の変更");
              setAddressChange(true);
            }
            if (res.facilityList?.[0]?.classChangeDate) {
              checkValue.push("施設種類の変更");
              setClassChange(true);
            }
            let changes = tableArr.find((item) => item.keyword === "changes")!;
            changes.setDefaultValue = checkValue;
            changes.checkBoxList = changes.checkBoxList!.map((item) => {
              return { ...item, disabled: true };
            });
          }
        } else {
          setShowButton(true);
        }

        setStateTableList([...tableArr]);
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  useEffect(() => {
    let tableArr = stateFakeTableList.current;
    let nameChangeInput = tableArr.filter(
      (_item, index) => index > 5 && index < 9
    )!;
    let addressChangeInput = tableArr.filter(
      (_item, index) => index > 8 && index < 14
    )!;
    let classChangeInput = tableArr.filter(
      (_item, index) => index > 13 && index !== 17
    )!;
    let transferedStaffJoinFlg = tableArr.find(
      (item) => item.keyword === "transferedStaffJoinFlg"
    )!;

    // 行の表示
    nameChangeInput.forEach((item) => (item.showLine = nameChange));
    addressChangeInput.forEach((item) => (item.showLine = addressChange));
    classChangeInput.forEach((item) => (item.showLine = classChange));
    transferedStaffJoinFlg.showLine = classChange && joinFlg;

    setStateTableList([...tableArr]);

    // バリデーションチェック
    let nameSchema = nameChange ? schema.nameChange : {};
    let addressSchema = addressChange ? schema.addressChange : {};
    let classSchema = classChange ? schema.classChange : {};
    let staffSchema = joinFlg ? schema.other : {};

    setStateSchema({
      ...schema.common,
      ...nameSchema,
      ...addressSchema,
      ...classSchema,
      ...staffSchema,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameChange, addressChange, classChange, joinFlg]);

  const makeHttpObj = (obj: any, index?: number): IAPI_FAC016_Request => {
    let tempSaveStep = 0;

    if (index === 0) {
      tempSaveStep = 1;
    }

    const facilityList = res.facilityList ? res.facilityList[0] : undefined;
    return {
      contractorNo: isKiko
        ? businessState.common?.contractorNo
        : state.authInfo?.contractorNo,
      applyId: res.applyId,
      tempSaveStep: tempSaveStep,
      facilityNo: facilityList?.facilityNo,
      addressChangeDate: obj.addressChangeDate,
      bZipcode: facilityList?.bZipcode,
      bPrefectureCd: facilityList?.bPrefectureCd,
      bAddressCity: facilityList?.bCity,
      bAdressOthers: facilityList?.bAdressOthers,
      aZipcode: obj.aZipcode,
      aPrefectureCd:
        Prefectures[obj.aAddress?.prefectures as keyof typeof Prefectures],
      aAddressCity: obj.aAddress?.city,
      aAdressOthers: obj.aAddress?.address,
      nameChangeDate: obj.nameChangeDate,
      bFacilityName: facilityList?.bFacilityName,
      aFacilityName: obj.aFacilityName,
      classChangeDate: obj.classChangeDate,
      bFacilityClassCd: facilityList?.bFacilityClassCd,
      aFacilityClassCd: facilityClass,
      transferedStaffJoinFlg: obj.transferedStaffJoinFlg,
      applyRelatedForm: obj.form?.map((item: any) => {
        return {
          serialNo: 1,
          facilityNo: facilityList?.facilityNo,
          formKbn: FormKbn["施設書類"],
          formClass: formClassFacility["施設情報の変更事項が確認できる書類"],
          fileName: item.formName,
          file: item.fileBase64,
        };
      }),
      screenId: "SCRC050017",
    };
  };

  // 契約者氏名等変更届・共済契約証書再交付申出施設情報更新API実行
  const callApiSubmit = async (type: "save" | "submit", date?: any) => {
    try {
      const resWithSaveOrSubmit = await httpIAPI_FAC016({
        ...httpReq.current,
        receptionDate: date,
      });

      if (resWithSaveOrSubmit.resultCd === "I0000") {
        setRes({
          ...res,
          applyId: resWithSaveOrSubmit?.applyId ?? res.applyId,
        });

        switch (type) {
          case "save":
            setOpenSave(true);
            break;

          case "submit":
            setOpenSubmit(false);
            navigate("/SCRC070008");
            break;
        }

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: resWithSaveOrSubmit.resultCd,
        });

        setOpenSubmit(false);
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleSubmit = async (data: any, index: any) => {
    setState({
      ...state,
      errorMsgId: "",
    });
    httpReq.current = { ...makeHttpObj(data, index) };

    switch (index) {
      case 0: // 一時保存
        callApiSubmit("save");
        break;

      case 1: // 機構へ提出する
        const oldFacilityClass = facilitiesfields.facilities.AllFacilities.find(
          (item: any) =>
            item.typecode === res.facilityList?.[0].bFacilityClassCd
        )?.partitioncode;
        const newFacilityClass = facilitiesfields.facilities.AllFacilities.find(
          (item: any) => item.typecode === facilityClass
        )?.partitioncode;
        if (
          oldFacilityClass === FacilityKbn["社会福祉施設等"] &&
          newFacilityClass !== FacilityKbn["社会福祉施設等"] &&
          classChange
        ) {
          setOpenWarning(true);
        } else {
          handleOpenSubmitModal();
        }
        break;
    }
  };

  const handleOpenSubmitModal = () => {
    setOpenWarning(false);
    setState({
      ...state,
      errorMsgId: "",
    });

    setOpenSubmit(true);
  };

  return (
    <Box data-testid="SCRC050017Test">
      <TkStepper
        steps={steps}
        activeStep={activeStep}
        savaStep={res.tempSaveStep}
      />

      <TkFakeTable
        list={stateTableList}
        ruleSchema={stateSchema}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          {
            submitButtonText: "一時保存",
            showButton: showButton,
          },
          {
            submitButtonText: isKiko ? "申請" : "機構へ提出する",
            showButton: showButton,
          },
        ]}
        onCheckAndSubmit={(data, index) => handleSubmit(data, index)}
      />

      <TkMessageDialog
        open={openSave}
        onOky={() => setOpenSave(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />

      {isKiko ? (
        <TkDateDialog
          open={openSubmit}
          onOky={(date: any) => callApiSubmit("submit", date)}
          onCancel={() => setOpenSubmit(false)}
          minDate={new Date("1900/01/01")}
          maxDate={new Date()}
          title="確認（申請）"
          contextText="　申請を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      ) : (
        <TkMessageDialog
          open={openSubmit}
          onOky={() => callApiSubmit("submit")}
          onCancel={() => setOpenSubmit(false)}
          title="確認（機構へ提出する）"
          contextText="　機構へ提出を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      )}

      <TkMessageDialog
        open={openWarning}
        onOky={handleOpenSubmitModal}
        onCancel={() => setOpenWarning(false)}
        onClose={() => setOpenWarning(false)}
        title="警告"
        contextText={TkPropertyMessageGet("TKMSG_SCR0001W")}
        okText="確認"
        cancelText="戻る"
      />
    </Box>
  );
};

export default SCRC050017;
